import React, { useEffect, useId, useReducer, useState } from 'react'
import { createPortal } from 'react-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons'

import { openPortalModal } from 'src/widgets/embed/openPortalModal'
import InputField from './InputField'
import { injectLocalPortalModal } from './injectLocalPortalModal'
import useValidation from './useValidation'

// Empty state — used both for the modal's initial form values when
// the donor hasn't opted in yet, and for Remove (which resets the
// committed state to this shape with optedIn: false).
const emptyState = () => ({ optedIn: false, businessName: '' })

const reducer = (state, action) => {
  switch (action.type) {
  case 'setFieldValue':
    return { ...state, [action.field]: action.value }
  case 'reset':
    return action.value
  default:
    throw new Error('Invalid reducer action')
  }
}

// Company-contribution composer. Pattern matches Tribute.jsx / Comment.jsx:
// a pill + Edit/Remove once a business name has been committed, or a
// trigger button when one hasn't. Returns null when the partner hasn't
// enabled `collect_business_name` (gated upstream by
// useCompanyContribution's `enabled` flag — which gates the
// draftOrderProps no-op too).
//
// State machine: useCompanyContribution (at App level) holds the
// *committed* { optedIn, businessName } pair; this component manages
// a *local* editing copy that initializes from the committed state on
// open. Save validates the local copy and commits via
// companyContribution.setState. Cancel discards. Remove clears
// committed state (sets optedIn: false).
const CompanyContribution = ({
  i18n,
  companyContribution,
  blank_modal_src,
  widget_instance_token,
}) => {
  if (!companyContribution.enabled) return null

  // Panel.Collection co-renders the active and transitioning panels
  // for ~330ms during step transitions, which means CompanyContribution
  // briefly coexists on multiple steps in the React tree. useId
  // disambiguates the modal id so the openPortalModal cache doesn't
  // merge them.
  const generatedId = useId()
  const modalId = `company-contribution-${generatedId}:${widget_instance_token}`

  const [ portal, setPortal ] = useState(null)
  const [ localState, dispatch ] = useReducer(reducer, emptyState())
  // Two-click confirmation for Remove, matching Tribute / Comment —
  // native confirm() can be silently blocked in non-sandboxed iframes.
  const [ confirmingRemove, setConfirmingRemove ] = useState(false)

  const validation = useValidation({
    i18n,
    fieldSet: { businessName: { required: true, maxLength: 100 } },
  })
  const businessNameError = validation.errors.businessName?.[0]

  const businessNameProps = {
    id: 'company_business_name',
    value: localState.businessName,
    onChange (e) {
      dispatch({ type: 'setFieldValue', field: 'businessName', value: e.target.value })
      validation.resetErrorsFor('businessName')
    },
  }

  const handleOpen = async () => {
    // Initialize local state from committed (or empty if no committed
    // contribution yet). We don't expose optedIn in the modal UI —
    // the presence of the modal IS the opt-in action.
    const initial = companyContribution.state.optedIn
      ? companyContribution.state
      : emptyState()
    dispatch({ type: 'reset', value: initial })
    validation.setErrors({})
    setConfirmingRemove(false)
    const result = await openPortalModal({
      id: modalId,
      src: blank_modal_src,
      ariaLabel: i18n.t('company_contribution.business_name'),
      maxWidth: '30rem',
      renderLocal: injectLocalPortalModal,
    })
    setPortal(result)
  }

  const handleSave = () => {
    if (!validation.validate(localState)) return
    companyContribution.setState({ ...localState, optedIn: true })
    portal?.close()
  }

  // Auto-revert the remove-confirmation state after 3 seconds so a
  // stale "Click to confirm" doesn't sit forever waiting for a
  // second click that never comes. Effect-managed (matches Comment.jsx)
  // so the timer is cleared if the component unmounts mid-window.
  useEffect(() => {
    if (!confirmingRemove) return
    const timer = setTimeout(() => setConfirmingRemove(false), 3000)
    return () => clearTimeout(timer)
  }, [ confirmingRemove ])

  const handleRemove = () => {
    if (!confirmingRemove) {
      setConfirmingRemove(true)
      return
    }
    setConfirmingRemove(false)
    companyContribution.setState(emptyState())
  }

  return (
    <div>
      {companyContribution.state.optedIn ? (
        <p className="text-success inline-flex flex-wrap items-center gap-x-2">
          <span className="inline-flex items-center gap-2">
            <FontAwesomeIcon icon={faCheckCircle} />
            {companyContribution.state.businessName}
          </span>
          <span aria-hidden="true" className="text-gray-400">·</span>
          <button
            type="button"
            onClick={handleOpen}
            className="btn-link underline underline-offset-2 text-sm"
          >
            Edit
          </button>
          <span aria-hidden="true" className="text-gray-400">·</span>
          <button
            type="button"
            onClick={handleRemove}
            className={`btn-link underline underline-offset-2 text-sm ${
              confirmingRemove ? 'text-danger font-semibold' : 'text-gray-500'
            }`}
          >
            {confirmingRemove ? 'Click to confirm' : 'Remove'}
          </button>
        </p>
      ) : (
        <button
          type="button"
          onClick={handleOpen}
          className="btn-link underline underline-offset-2 text-start"
        >
          {i18n.t('company_contribution.enable_label')}
        </button>
      )}

      {portal?.target && createPortal(
        <>
          <button
            type="button"
            className="widget-modal-close hide-in-blank"
            aria-label={i18n.t('close')}
            onClick={() => portal.close()}
          />
          <div className="px-3 py-4 chrome:px-4 chrome:py-5 space-y-3">
            <h2 className="text-xl font-semibold">
              {i18n.t('company_contribution.business_name')}
            </h2>

            <InputField
              {...businessNameProps}
              label={i18n.t('company_contribution.business_name')}
              required
              error={businessNameError}
              hideLabel
              autoFocus
            />

            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => portal.close()}
                className="btn btn-outline-primary flex-1"
              >
                {i18n.t('cancel')}
              </button>
              <button
                type="button"
                onClick={handleSave}
                className="btn btn-primary flex-1"
              >
                Save
              </button>
            </div>
          </div>
        </>,
        portal.target
      )}
    </div>
  )
}

export default CompanyContribution
